import {
	BundleItem,
	GetCollectionsShowResponseAvailableInBundles,
	InAppPurchaseWebPrice,
} from 'mytours-read-api-typescript'

import { BundleRequestParams } from '../api/actions'

/** The generated name is the response path the type sits on; this is what the app calls it. */
export type BundleOffer = GetCollectionsShowResponseAvailableInBundles

/**
 * Per-bundle redemption state.
 *
 * Mirrors the `CollectionCoupons` shape used by the React Native app, adapted for
 * bundles. `items` is the bundle's contents list resolved from the IAP catalogue
 * (see the `fetchInAppPurchases` reconcile in the reducer).
 */
export interface RedeemedBundleEntry {
	readonly hasUnlockedBundle: boolean
	readonly expireAt?: number
	readonly redeemingCoupon: boolean
	readonly redeemCouponError?: Error
	readonly couponUsedToUnlock?: string
	readonly items?: BundleItem[]
	// Original redeem timestamp (ms); every item in the bundle inherits it for recency.
	readonly addedAt?: number
}

/** Redemption state of every known bundle, keyed by bundle id. */
export type RedeemedBundles = {
	[id: string]: RedeemedBundleEntry | undefined
}

/**
 * Purchasability of each bundle, keyed by bundle id, from the IAP catalogue (`fetchInAppPurchases`) or
 * from fetching the bundle itself (`fetchBundle`). Lets the redeem dialog show a "Buy bundle" button for
 * bundles that are sold via Stripe (`web_price`), independently of whether they've been redeemed yet. A
 * bundle with no active `web_price` is redeem-only.
 */
export type BundleProducts = {
	[id: string]:
		| {
				readonly active: boolean
				readonly webPrice?: InAppPurchaseWebPrice
				/** The bundle's own name, which is what the unlock button says — not the product's marketing title. */
				readonly name?: string
				/** Contents, from the catalogue or `fetchBundle`. Here as well as on the redeemed entry because a
				 * complete catalogue prunes entries it cannot name, and leaves this map alone. */
				readonly items?: BundleItem[]
		  }
		| undefined
}

/**
 * What each collection sells for on its own, keyed by collection id, from the IAP catalogue. Only collections
 * that are actually for sale are here — it is what a bundle's saving is measured against.
 */
export type CollectionWebPrices = {
	[id: string]: InAppPurchaseWebPrice | undefined
}

/** Tries per bundle before the sync gives up, the first attempt included. */
export const MAX_BUNDLE_FETCH_ATTEMPTS = 3

export type BundleFetchFailure = {
	/** The whole request: nothing else still holds the bundle's version and account by the time a sync
	 * comes round to replay it. */
	readonly params: BundleRequestParams
	/** Tries so far, replays included. */
	readonly attempts: number
	/** A 4xx is the server's answer about this bundle rather than a blip, so there is nothing to replay. */
	readonly permanent: boolean
}

export type BundleFetchFailures = {
	[id: string]: BundleFetchFailure | undefined
}
